import BaseImporter from "./base-importer";
import SportService from "../sport-service";
import PlayerService from "../player-service";
import { isNumber } from "../common/object-util";
import { compareByFullName } from "../../sports/common/utils/player-utils";
import ContextService from "../context-service";
import { playerRepository } from "../../storage/storage";

const FIELDS = {
    FIRST_NAME: "firstName",
    LAST_NAME: "lastName",
    NUMBER: "number",
    YEAR: "year",
    NATIONAL_RANKING: "nationalRanking",
    REGIONAL_RANKING: "regionalRanking",
    CONFERENCE_RANKING: "conferenceRanking",
    STARTER: "starter",
    PLAYER_MATCH: "playerMatch",
    SINGLE: "single",
    DOUBLES: "doubles"
};

const requiredHeaders = [FIELDS.FIRST_NAME, FIELDS.LAST_NAME, FIELDS.YEAR];
const optionalHeaders = [
    FIELDS.NUMBER,
    FIELDS.NATIONAL_RANKING,
    FIELDS.REGIONAL_RANKING,
    FIELDS.CONFERENCE_RANKING,
    FIELDS.STARTER,
    FIELDS.SINGLE,
    FIELDS.DOUBLES
];

class TennisImporter extends BaseImporter {
    getRequiredHeaders() {
        return requiredHeaders;
    }

    getOptionalHeaders() {
        return optionalHeaders;
    }

    isValidPlayerMatch(records = [], r) {
        const { playerMatch } = r;

        if (playerMatch) {
            const selectedPlayers = records
                .filter((s) => s.playerMatch && s.importIndex !== r.importIndex)
                .map((s) => s.playerMatch);

            return !selectedPlayers.find((sp) =>
                r.number
                    ? sp.number === r.number
                    : this.compareByFullName(sp, r) === 0
            );
        }

        return true;
    }

    validateRecord(records, r) {
        r.errors = {};

        if (this.isEmpty(r, FIELDS.FIRST_NAME)) {
            Object.assign(r.errors, {
                [FIELDS.FIRST_NAME]: ["First name is required"]
            });
        }
        if (this.isEmpty(r, FIELDS.LAST_NAME)) {
            Object.assign(r.errors, {
                [FIELDS.LAST_NAME]: ["Last name is required"]
            });
        }
        if (
            r[FIELDS.NUMBER] &&
            this.isFieldDuplicated(records, r, FIELDS.NUMBER)
        ) {
            Object.assign(r.errors, {
                [FIELDS.NUMBER]: ["Number duplicated"]
            });
        }
        if (
            r[FIELDS.NUMBER] &&
            !this.matches(r, FIELDS.NUMBER, /^[A-Za-z0-9]{1,3}$/)
        ) {
            Object.assign(r.errors, {
                [FIELDS.NUMBER]: [
                    "Number must contain up to 3 alpha characters"
                ]
            });
        }
        if (
            !this.isEmpty(r, FIELDS.NATIONAL_RANKING) &&
            !isNumber(Number(r[FIELDS.NATIONAL_RANKING]))
        ) {
            Object.assign(r.errors, {
                [FIELDS.NATIONAL_RANKING]: ["National Ranking must be a number"]
            });
        }
        if (
            !this.isEmpty(r, FIELDS.REGIONAL_RANKING) &&
            !isNumber(Number(r[FIELDS.REGIONAL_RANKING]))
        ) {
            Object.assign(r.errors, {
                [FIELDS.REGIONAL_RANKING]: ["Regional Ranking must be a number"]
            });
        }
        if (
            !this.isEmpty(r, FIELDS.CONFERENCE_RANKING) &&
            !isNumber(Number(r[FIELDS.CONFERENCE_RANKING]))
        ) {
            Object.assign(r.errors, {
                [FIELDS.CONFERENCE_RANKING]: [
                    "Conference Ranking must be a number"
                ]
            });
        }

        if (!this.isValidPlayerMatch(records, r)) {
            Object.assign(r.errors, {
                [FIELDS.PLAYER_MATCH]: ["Player already selected"]
            });
        }
    }

    validateRecords(records) {
        records.forEach((r) => this.validateRecord(records, r));
    }

    findPlayerMatch(csvRecord, players) {
        const number = this.readCsvValue(csvRecord, FIELDS.NUMBER);
        return players.find((ep) =>
            number
                ? ep.uniform === number
                : compareByFullName(
                      {
                          firstName: this.readCsvValue(
                              csvRecord,
                              FIELDS.FIRST_NAME
                          ),
                          lastName: this.readCsvValue(
                              csvRecord,
                              FIELDS.LAST_NAME
                          )
                      },
                      ep
                  ) === 0
        );
    }

    findYearMatch(player, years) {
        return player.year
            ? years.find((y) => player.year.toUpperCase() === y.code)
            : null;
    }

    mapCsvPlayer(csvPlayer, years, players, index) {
        return {
            importIndex: index,
            number: this.readCsvValue(csvPlayer, FIELDS.NUMBER),
            firstName: this.readCsvValue(csvPlayer, FIELDS.FIRST_NAME),
            lastName: this.readCsvValue(csvPlayer, FIELDS.LAST_NAME),
            nationalRanking: this.readCsvValue(
                csvPlayer,
                FIELDS.NATIONAL_RANKING
            ),
            regionalRanking: this.readCsvValue(
                csvPlayer,
                FIELDS.REGIONAL_RANKING
            ),
            conferenceRanking: this.readCsvValue(
                csvPlayer,
                FIELDS.CONFERENCE_RANKING
            ),
            starter: this.readCsvValue(csvPlayer, FIELDS.STARTER),
            playerMatch: this.findPlayerMatch(csvPlayer, players),
            year: this.findYearMatch(csvPlayer, years),
            single: this.readCsvValue(csvPlayer, FIELDS.SINGLE),
            doubles: this.readCsvValue(csvPlayer, FIELDS.DOUBLES),
            errors: {}
        };
    }

    async mapCsvPlayers(teamId, sportId, data) {
        const years = await SportService.getPlayerYears();
        const players = await PlayerService.getPlayers(teamId);

        return data.map((d, index) =>
            this.mapCsvPlayer(d, years, players, index)
        );
    }

    async savePlayers(teamId, players) {
        const toUpdate = players.map((p) => {
            const player = p.playerMatch || { teamId };

            player.uniform = p.number;
            player.firstName = p.firstName;
            player.lastName = p.lastName;
            player.nationalRanking = p.nationalRanking;
            player.regionalRanking = p.regionalRanking;
            player.conferenceRanking = p.conferenceRanking;
            player.starter = p.starter;
            player.year = p.year;

            return player;
        });

        return PlayerService.savePlayers(teamId, toUpdate);
    }

    async setGameData(gameId, teamId, importedPlayers) {
        const gameContext = await ContextService.getGameContext(gameId);
        const teamPlayers = await playerRepository.where({ teamId }).toArray();
        const ttc = gameContext.getTeamContext(teamId);
        //
        importedPlayers.forEach((importedPlayer) => {
            const savedPlayer = teamPlayers.find((ep) =>
                importedPlayer.number
                    ? ep.uniform === importedPlayer.number
                    : compareByFullName(importedPlayer, ep) === 0
            );

            if (!savedPlayer) return;

            const { single, doubles } = importedPlayer;
            single ? ttc.addSingle(savedPlayer) : ttc.removeSingle(savedPlayer);
            doubles
                ? ttc.addDoubles(savedPlayer)
                : ttc.removeDoubles(savedPlayer);
        });
        //
        return ContextService.updateContext(gameContext);
    }
}

export default new TennisImporter();
